<template>
  <DxPopup
    :visible="visible"
    title="Attachments"
    :height="'auto'"
    width="50%"
    :wrapperAttr="{
      class: 'css-v2 gc-popup gc-popup-distribution-manifest-files',
    }"
    :hide-on-outside-click="false"
    :showCloseButton="false"
    :drag-enabled="false"
    @hidden="onClose"
  >
    <DxToolbarItem
      v-for="(item, index) in getToolbarItems"
      :key="index"
      :widget="item.widget"
      :location="item.location"
      :visible="item.visible"
      :toolbar="item.position"
      :options="item.options"
    />
    <div class="css-v2 gc-popup-content">
      <div class="gc-popup-content-inner">
        <div class="gc-popup-header" v-if="!isReadOnly && isUserPermissionToEdit">
          <div
            class="dmf-drop-zone"
            :class="{ 'is-dragging': isDragging }"
            @click="triggerFileInput"
            @dragover.prevent="isDragging = true"
            @dragleave.prevent="isDragging = false"
            @drop.prevent="onDrop"
          >
            <input
              ref="fileInput"
              type="file"
              multiple
              accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
              style="display: none"
              @change="onFilesSelected"
            />
            <svg viewBox="0 0 24 24">
              <path
                d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"
              />
            </svg>
            Drop files here or <span class="dz-link">browse</span> — JPEG, PNG,
            PDF, Word, Excel, CSV, TXT
            <span class="dz-hint">10 MB each</span>
          </div>
          <div v-if="errorMsg" class="dmf-error-msg">{{ errorMsg }}</div>
        </div>

        <div class="gc-popup-body" v-if="localFiles.length > 0">
          <div
            class="file-list-container"
            v-for="(file, index) in localFiles"
            :key="file.RecordId || index"
          >
            <div class="file-item">
              <div
                class="icon"
                v-html="getFileIcon(file.ContentType)"
                @click="handleFileOpen(file)"
              ></div>
              <div class="file-name" @click="handleFileOpen(file)">
                <span :title="file.FileName">{{ file.FileName }}</span>
                <span>{{ formatSize(file.FileSize) }}</span>
              </div>
              <div
                class="file-action"
                @click="removeFile(index)"
                v-if="!isReadOnly"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 40 40"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M23.3333 14.9998V14.3332C23.3333 13.3997 23.3333 12.933 23.1517 12.5765C22.9919 12.2629 22.7369 12.0079 22.4233 11.8482C22.0668 11.6665 21.6001 11.6665 20.6667 11.6665H19.3333C18.3999 11.6665 17.9332 11.6665 17.5767 11.8482C17.2631 12.0079 17.0081 12.2629 16.8483 12.5765C16.6667 12.933 16.6667 13.3997 16.6667 14.3332V14.9998M18.3333 19.5832V23.7498M21.6667 19.5832V23.7498M12.5 14.9998H27.5M25.8333 14.9998V24.3332C25.8333 25.7333 25.8333 26.4334 25.5608 26.9681C25.3212 27.4386 24.9387 27.821 24.4683 28.0607C23.9335 28.3332 23.2335 28.3332 21.8333 28.3332H18.1667C16.7665 28.3332 16.0665 28.3332 15.5317 28.0607C15.0613 27.821 14.6788 27.4386 14.4392 26.9681C14.1667 26.4334 14.1667 25.7333 14.1667 24.3332V14.9998"
                    stroke="#D92D20"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>

        <div class="gc-popup-body-empty" v-else>
          <span>{{
            isReadOnly
              ? "No attachments on this manifest."
              : "No files uploaded"
          }}</span>
        </div>
      </div>
    </div>

    <gc-image-preview-modal
      :visible="isImagePreviewVisible"
      :file="previewImage"
      @close="closeImagePreview"
      v-if="isImagePreviewVisible"
    />
  </DxPopup>
</template>

<script>
import { DxPopup, DxToolbarItem } from "devextreme-vue/popup";
import gcImagePreviewModal from "@/popups/purchasing/gc-popup-sup-inv-img-preview.vue";
import appConfig from "@/app-config";
import { DistributionManifestService } from "@/_services/api/sales/distribution-manifest.service";
import { FeatureStorage } from "../../_services/storage";

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const ACCEPTED_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "application/pdf",
  "text/plain",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/csv",
];

export default {
  name: "GcPopupDistributionManifestFiles",
  components: {
    DxPopup,
    DxToolbarItem,
    gcImagePreviewModal,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    recordId: {
      type: String,
      default: null,
    },
    isReadOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      localFiles: [],
      isDragging: false,
      errorMsg: "",
      errorTimer: null,
      isImagePreviewVisible: false,
      previewImage: null,
    };
  },
  created() {
    this.loadFiles();
  },
  methods: {
    async loadFiles() {
      if (!this.recordId) {
        this.localFiles = [];
        return;
      }
      try {
        const response = await DistributionManifestService.getAttachedFiles(
          this.recordId,
        );
        this.localFiles = (response.data.value || []).slice();
      } catch (error) {
        this.$emit("show-error", error, "Failed to load attachments");
      }
    },
    onClose() {
      this.$emit("close");
    },
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    onFilesSelected(e) {
      const files = Array.from(e.target.files);
      this.processFiles(files);
      e.target.value = "";
    },
    onDrop(e) {
      this.isDragging = false;
      const files = Array.from(e.dataTransfer.files);
      this.processFiles(files);
    },
    processFiles(files) {
      files.forEach((file) => {
        if (ACCEPTED_TYPES.indexOf(file.type) === -1) {
          this.showError(
            `"${file.name}" is not a supported file type. Please upload JPEG, PNG, PDF, Word, Excel, CSV, or TXT.`,
          );
          return;
        }
        if (file.size > MAX_FILE_SIZE) {
          this.showError(`"${file.name}" exceeds the 10 MB limit.`);
          return;
        }
        this.readFileAsBase64(file);
      });
    },
    readFileAsBase64(file) {
      const reader = new FileReader();
      reader.onload = async (e) => {
        this.localFiles.push({
          RecordId: "00000000-0000-0000-0000-000000000000",
          FileName: file.name,
          ContentType: file.type,
          FileSize: file.size,
          MediaBase64String: e.target.result,
        });
        await this.persistFiles();
      };
      reader.readAsDataURL(file);
    },
    async removeFile(index) {
      const confirmed = await this.$confirm(
        "Are you sure you want to delete this file? This action cannot be undone.",
        "Delete File",
      );
      if (!confirmed) return;
      this.localFiles.splice(index, 1);
      await this.persistFiles();
    },
    async persistFiles() {
      this.$emit("show-loading", true);
      const payload = {
        TransactionRecordId: this.recordId,
        TransactionType: "DistributionManifest",
        TransactionMediaMetas: this.localFiles.map((file) => ({
          TransactionRecordId: this.recordId,
          TransactionType: "DistributionManifest",
          RecordId: file.RecordId || "00000000-0000-0000-0000-000000000000",
          ContentType: file.ContentType,
          FileName: file.FileName,
          FileSize: file.FileSize,
          MediaBase64String: file.MediaBase64String || undefined,
        })),
      };

      try {
        await DistributionManifestService.updateTransactionMediaMeta(payload);
        await this.loadFiles();
      } catch (error) {
        this.$emit("show-error", error, "Failed to update attachments");
      } finally {
        this.$emit("show-loading", false);
      }
    },
    handleFileOpen(file) {
      const contentType = file.ContentType || "";
      if (
        contentType === "image/png" ||
        contentType === "image/jpeg" ||
        contentType === "image/jpg"
      ) {
        const downloadApiUrl =
          file.BlobName && file.MediaUrl
            ? appConfig.VUE_APP_ROOT_API +
              "/api/TransactionMedia/" +
              file.BlobName +
              "/download"
            : null;
        this.previewImage = {
          name: file.FileName,
          base64: file.MediaBase64String || file.MediaUrl,
          MediaUrl: file.MediaUrl || null,
          DownloadApiUrl: downloadApiUrl,
        };
        this.isImagePreviewVisible = true;
      } else if (
        contentType === "application/pdf" ||
        contentType === "text/plain"
      ) {
        if (file.MediaUrl) {
          window.open(file.MediaUrl, "_blank", "noopener,noreferrer");
        } else if (file.MediaBase64String) {
          const base64Str = file.MediaBase64String;
          const commaIdx = base64Str.indexOf(",");
          const base64Data =
            commaIdx !== -1 ? base64Str.slice(commaIdx + 1) : base64Str;
          const binaryStr = atob(base64Data);
          const bytes = new Uint8Array(binaryStr.length);
          for (let i = 0; i < binaryStr.length; i++) {
            bytes[i] = binaryStr.charCodeAt(i);
          }
          const blob = new Blob([bytes], { type: contentType });
          const blobUrl = URL.createObjectURL(blob);
          window.open(blobUrl, "_blank", "noopener,noreferrer");
        }
      } else {
        const dlSrc = file.MediaBase64String || file.MediaUrl;
        if (!dlSrc) return;
        const link = document.createElement("a");
        link.href = dlSrc;
        link.download = file.FileName || "download";
        link.target = "_blank";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    },
    closeImagePreview() {
      this.isImagePreviewVisible = false;
      this.previewImage = null;
    },
    showError(msg) {
      this.errorMsg = msg;
      clearTimeout(this.errorTimer);
      this.errorTimer = setTimeout(() => {
        this.errorMsg = "";
      }, 5000);
    },
    formatSize(bytes) {
      if (!bytes) return "";
      if (bytes < 1024) return bytes + " B";
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
      return (bytes / (1024 * 1024)).toFixed(1) + " MB";
    },
    getFileIcon(contentType) {
      const icons = {
        "application/pdf":
          '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#D92D20" stroke-width="1.5"><path d="M6 2h9l5 5v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z"/><path d="M15 2v5h5"/></svg>',
        "image/jpeg":
          '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#7F56D9" stroke-width="1.5"><path d="M6 2h9l5 5v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z"/><path d="M15 2v5h5"/><circle cx="10" cy="13" r="2"/></svg>',
        "image/jpg":
          '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#7F56D9" stroke-width="1.5"><path d="M6 2h9l5 5v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z"/><path d="M15 2v5h5"/><circle cx="10" cy="13" r="2"/></svg>',
        "image/png":
          '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#7F56D9" stroke-width="1.5"><path d="M6 2h9l5 5v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z"/><path d="M15 2v5h5"/><circle cx="10" cy="13" r="2"/></svg>',
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
          '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#155EEF" stroke-width="1.5"><path d="M6 2h9l5 5v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z"/><path d="M15 2v5h5"/><path d="M8 13h1l1 4 1-4h1l1 4 1-4h1"/></svg>',
        "application/msword":
          '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#155EEF" stroke-width="1.5"><path d="M6 2h9l5 5v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z"/><path d="M15 2v5h5"/><path d="M8 13h1l1 4 1-4h1l1 4 1-4h1"/></svg>',
        "application/vnd.ms-excel":
          '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#079455" stroke-width="1.5"><path d="M6 2h9l5 5v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z"/><path d="M15 2v5h5"/><path d="M8 13l4 6M12 13l-4 6"/></svg>',
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
          '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#079455" stroke-width="1.5"><path d="M6 2h9l5 5v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z"/><path d="M15 2v5h5"/><path d="M8 13l4 6M12 13l-4 6"/></svg>',
        "text/csv":
          '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#079455" stroke-width="1.5"><path d="M6 2h9l5 5v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z"/><path d="M15 2v5h5"/><path d="M8 13h8M8 16h8M8 19h5"/></svg>',
      };
      const fallback =
        '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#35BAF6" stroke-width="1.5"><path d="M6 2h9l5 5v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z"/><path d="M15 2v5h5"/></svg>';
      return contentType && icons[contentType] ? icons[contentType] : fallback;
    },
  },
  computed: {
    getToolbarItems() {
      return [
        {
          widget: "dxButton",
          location: "after",
          toolbar: "top",
          visible: true,
          options: {
            text: "",
            icon: "close",
            stylingMode: "text",
            type: "default",
            onClick: this.onClose,
          },
        },
      ];
    },
    isUserPermissionToEdit() {
      return FeatureStorage.checkPermission("DistributionManifest", "create");
    },
  },
  beforeDestroy() {
    clearTimeout(this.errorTimer);
  },
};
</script>

<style lang="scss">
.gc-popup-distribution-manifest-files {
  .dx-popup-content {
    .gc-popup-content {
      padding: 12px !important;
      height: 100%;

      .gc-popup-content-inner {
        background-color: white;
        height: 100%;

        .gc-popup-header {
          padding: 12px;
        }

        .dmf-drop-zone {
          border: 1px dashed #d0d5dd;
          border-radius: 2px;
          padding: 20px 16px;
          text-align: center;
          color: #667085;
          font-size: 12px;
          cursor: pointer;
          background: #fff;
          transition: background 0.1s, border-color 0.1s;

          &:hover,
          &.is-dragging {
            background: #f0f9ff;
            border-color: #35baf6;
          }

          svg {
            width: 20px;
            height: 20px;
            stroke: #98a2b3;
            fill: none;
            stroke-width: 2;
            stroke-linecap: round;
            stroke-linejoin: round;
            display: block;
            margin: 0 auto 8px;
          }

          .dz-link {
            color: #35baf6;
            font-weight: 500;
          }

          .dz-hint {
            display: block;
            font-size: 11px;
            color: #98a2b3;
            margin-top: 4px;
          }
        }

        .dmf-error-msg {
          font-size: 12px;
          padding: 6px 0 0;
          color: #dc2626;
        }

        .gc-popup-body {
          padding: 12px;
          max-height: 400px;
          overflow-y: auto;
          overscroll-behavior: contain;
          -webkit-overflow-scrolling: touch;

          .file-item {
            border: 1px solid var(--Colors-Border-border-secondary, #eaecf0);
            display: flex;
            justify-content: space-between;
            gap: 15px;
            align-items: center;
            padding: 10px 6px 6px 6px;

            .file-name {
              width: 100%;
              display: flex;
              flex-direction: column;

              > span:first-child {
                font-family: Inter;
                font-weight: 500;
                font-size: 14px;
                line-height: 20px;
                color: var(--colors-text-text-secondary-700, #344054);
              }

              > span:last-child {
                font-family: Inter;
                font-weight: 400;
                font-size: 12px;
                line-height: 18px;
                color: var(--colors-text-text-tertiary-600, #475467);
              }
            }

            .icon {
              cursor: pointer;
            }
          }

          .file-list-container:not(:last-child) {
            margin-bottom: 12px;
          }

          .file-action {
            cursor: pointer;
          }
        }
      }
    }

    .gc-popup-body-empty {
      padding: 12px;

      > span {
        font-family: Inter;
        font-weight: 400;
        font-size: 12px;
        line-height: 18px;
        text-align: center;
        color: var(--colors-text-text-tertiary-600, #475467);
      }
    }
  }
}
</style>
